import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Building2, Mic2, CalendarCheck, DollarSign, Check, X, TrendingUp, MapPin, Loader2,
} from "lucide-react";
import { api } from "../lib/api";
import { Stat, StatusBadge } from "../components/shared";
import { useLang } from "../lib/i18n";

export default function AdminDashboard() {
  const { t } = useLang();
  const d = t.dash.admin;
  const [data, setData] = useState(null);
  const [artists, setArtists] = useState([]);
  const [venues, setVenues] = useState([]);

  const load = () => {
    api.platformAnalytics().then(setData);
    api.artists().then(setArtists);
    api.venues().then(setVenues);
  };
  useEffect(load, []);

  const approve = async (id, status) => {
    await api.setArtistStatus(id, status);
    toast.success(`Artist ${status}`);
    load();
  };

  if (!data) return <div className="h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin gold" size={32} /></div>;

  const pending = artists.filter((a) => a.status === "pending");

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <p className="text-xs uppercase tracking-[0.3em] gold mb-2">{d.eyebrow}</p>
      <h1 className="font-serif-lux text-4xl sm:text-5xl font-light mb-8">{d.title}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat testid="admin-venues" label={d.venues} value={data.total_venues} sub="Registered" />
        <Stat testid="admin-artists" label={d.artists} value={data.total_artists} sub={`${data.pending_artists} pending`} />
        <Stat testid="admin-bookings" label={d.bookings} value={data.total_bookings} sub="All time" />
        <Stat testid="admin-gmv" label={d.grossBookings} value={`£${data.total_revenue}`} sub="Artist fees" />
        <Stat testid="admin-revenue" label={d.platformRevenue} value={`£${data.platform_revenue}`} sub="Fees + commission" />
      </div>

      {/* Approvals */}
      <div className="card-lux p-6 mb-10">
        <p className="text-xs uppercase tracking-[0.3em] gold mb-4">{d.pendingApprovals}</p>
        {pending.length === 0 ? <p className="text-sm text-neutral-500">{d.allReviewed} 🎉</p> : (
          <div className="space-y-3" data-testid="pending-approvals">
            {pending.map((a) => (
              <div key={a.id} className="flex items-center justify-between border border-white/10 rounded-sm p-4">
                <div className="flex items-center gap-3">
                  <img src={a.photo} alt={a.name} className="w-10 h-10 rounded-sm object-cover" />
                  <div><p className="text-sm font-medium">{a.name}</p><p className="text-xs text-neutral-500">{a.style} · {a.city}</p></div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`approve-${a.id}`} onClick={() => approve(a.id, "approved")} className="btn-gold px-3 py-1.5 rounded-sm text-xs flex items-center gap-1"><Check size={13} /> {d.approve}</button>
                  <button data-testid={`reject-${a.id}`} onClick={() => approve(a.id, "declined")} className="btn-ghost-lux px-3 py-1.5 rounded-sm text-xs flex items-center gap-1"><X size={13} /> {d.reject}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-10">
        {/* Top artists */}
        <div className="card-lux p-6">
          <p className="text-sm text-neutral-400 mb-5 flex items-center gap-2"><Mic2 size={16} className="gold" /> {d.topArtists}</p>
          <div className="space-y-4" data-testid="top-artists-chart">
            {data.top_artists.map((a) => {
              const max = Math.max(...data.top_artists.map((x) => x.tips), 1);
              return (
                <div key={a.name}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-neutral-300">{a.name}</span><span className="gold">£{a.tips}</span>
                  </div>
                  <div className="h-2.5 bg-white/5 overflow-hidden rounded-sm">
                    <div className="h-full bg-gold transition-all" style={{ width: `${(a.tips / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* By city */}
        <div className="card-lux p-6">
          <p className="text-sm text-neutral-400 mb-5 flex items-center gap-2"><MapPin size={16} className="gold" /> {d.byCity}</p>
          <div className="space-y-4" data-testid="city-chart">
            {data.by_city.map((c) => {
              const max = Math.max(...data.by_city.map((x) => x.bookings), 1);
              return (
                <div key={c.city}>
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-neutral-300">{c.city}</span><span className="gold">{c.bookings} {d.bookingsWord}</span>
                  </div>
                  <div className="h-2.5 bg-white/5 overflow-hidden rounded-sm">
                    <div className="h-full bg-gold transition-all" style={{ width: `${(c.bookings / max) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Active venues */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-4 flex items-center gap-2"><Building2 size={14} /> {d.mostActive}</p>
          <div className="card-lux divide-y divide-white/10" data-testid="active-venues-list">
            {data.active_venues.map((v, i) => (
              <div key={i} className="flex items-center justify-between p-4">
                <div><p className="text-sm font-medium">{v.name}</p><p className="text-xs text-neutral-500">{v.city}</p></div>
                <span className="text-sm gold">{v.bookings} {d.bookingsWord}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs uppercase tracking-[0.3em] gold mb-4 flex items-center gap-2"><TrendingUp size={14} /> {d.leaderboard}</p>
          <div className="card-lux divide-y divide-white/10" data-testid="artist-leaderboard">
            {data.top_artists.map((a, i) => (
              <div key={i} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <span className="font-serif-lux text-xl gold w-6">{i + 1}</span>
                  <div><p className="text-sm font-medium">{a.name}</p><p className="text-xs text-neutral-500">{a.style}</p></div>
                </div>
                <span className="text-sm text-neutral-400">★ {a.rating}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
